/**
 * Hook composto para gerenciar todos os dados do Dashboard
 * 
 * Combina todos os hooks individuais do dashboard em uma única interface,
 * fornecendo estados agregados de loading/error e funções de invalidação coordenada.
 * 
 * @module hooks/useDashboardData
 */

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDashboard, DASHBOARD_SUMMARY_QUERY_KEY } from './useDashboard';
import { useTaskDistribution, TASK_DISTRIBUTION_QUERY_KEY } from './data/useTaskDistribution';
import { useWorkloadByProfessional, WORKLOAD_BY_PROFESSIONAL_QUERY_KEY } from './data/useWorkloadByProfessional';
import { useEquipmentStatusSummary, EQUIPMENT_STATUS_SUMMARY_QUERY_KEY } from './data/useEquipmentStatusSummary';
import { usePontoDesvios, PONTO_DESVIOS_QUERY_KEY } from './data/usePontoDesvios';
import { useEstoqueAlertas, ESTOQUE_ALERTAS_QUERY_KEY } from './data/useEstoqueAlertas';

// Todas as query keys do dashboard para invalidação coordenada
export const ALL_DASHBOARD_QUERY_KEYS = [
    DASHBOARD_SUMMARY_QUERY_KEY,
    TASK_DISTRIBUTION_QUERY_KEY,
    WORKLOAD_BY_PROFESSIONAL_QUERY_KEY,
    EQUIPMENT_STATUS_SUMMARY_QUERY_KEY,
    PONTO_DESVIOS_QUERY_KEY,
    ESTOQUE_ALERTAS_QUERY_KEY,
];

/**
 * Hook composto para gerenciar todos os dados do Dashboard
 * 
 * Este hook combina todos os hooks individuais do dashboard, fornecendo:
 * - Estados agregados de loading e error
 * - Dados de todos os hooks em um único objeto
 * - Funções de refetch e invalidação coordenada
 * - Retry inteligente baseado no estado agregado
 * 
 * @returns {object} Objeto com todos os dados e estados do dashboard
 * @property {object} data - Objeto contendo todos os dados do dashboard
 * @property {object} data.summary - Dados do resumo (KPIs)
 * @property {Array} data.taskDistribution - Distribuição de tarefas por tipo
 * @property {Array} data.workloadByProfessional - Carga de trabalho por profissional
 * @property {Array} data.equipmentStatusSummary - Status dos equipamentos
 * @property {Array} data.pontoDesvios - Desvios de ponto recentes
 * @property {Array} data.estoqueAlertas - Alertas de estoque
 * @property {boolean} isLoading - Indica se qualquer hook está carregando
 * @property {boolean} isError - Indica se qualquer hook teve erro
 * @property {Array} errors - Array com todos os erros (pode ter múltiplos)
 * @property {object} loadingStates - Objeto com estados de loading individuais
 * @property {object} errorStates - Objeto com estados de error individuais
 * @property {function} refetchAll - Função para recarregar todos os dados
 * @property {function} invalidateAll - Função para invalidar todos os caches
 * 
 * @example
 * ```javascript
 * const {
 *     data,
 *     isLoading,
 *     isError,
 *     errors,
 *     refetchAll,
 *     invalidateAll
 * } = useDashboardData();
 * 
 * if (isLoading) return <LoadingIndicator />;
 * if (isError) return <ErrorDisplay errors={errors} />;
 * 
 * // Usar os dados
 * const { summary, taskDistribution } = data;
 * 
 * // Invalidar após mutação
 * await createTask(taskData);
 * invalidateAll();
 * ```
 */
export const useDashboardData = () => {
    const queryClient = useQueryClient();

    // Chamar todos os hooks individuais
    const summaryHook = useDashboard();
    const taskDistributionHook = useTaskDistribution();
    const workloadHook = useWorkloadByProfessional();
    const equipmentHook = useEquipmentStatusSummary();
    const pontoDesviosHook = usePontoDesvios();
    const estoqueAlertasHook = useEstoqueAlertas();

    // Agregar dados
    const data = useMemo(() => ({
        summary: summaryHook.summary,
        taskDistribution: taskDistributionHook.data,
        workloadByProfessional: workloadHook.data,
        equipmentStatusSummary: equipmentHook.data,
        pontoDesvios: pontoDesviosHook.data,
        estoqueAlertas: estoqueAlertasHook.data,
    }), [
        summaryHook.summary,
        taskDistributionHook.data,
        workloadHook.data,
        equipmentHook.data,
        pontoDesviosHook.data,
        estoqueAlertasHook.data,
    ]);

    // Estados de loading individuais
    const loadingStates = useMemo(() => ({
        summary: summaryHook.isLoading,
        taskDistribution: taskDistributionHook.isLoading,
        workloadByProfessional: workloadHook.isLoading,
        equipmentStatusSummary: equipmentHook.isLoading,
        pontoDesvios: pontoDesviosHook.isLoading,
        estoqueAlertas: estoqueAlertasHook.isLoading,
    }), [
        summaryHook.isLoading,
        taskDistributionHook.isLoading,
        workloadHook.isLoading,
        equipmentHook.isLoading,
        pontoDesviosHook.isLoading,
        estoqueAlertasHook.isLoading,
    ]);

    // Estados de error individuais
    const errorStates = useMemo(() => ({
        summary: summaryHook.error,
        taskDistribution: taskDistributionHook.error,
        workloadByProfessional: workloadHook.error,
        equipmentStatusSummary: equipmentHook.error,
        pontoDesvios: pontoDesviosHook.error,
        estoqueAlertas: estoqueAlertasHook.error,
    }), [
        summaryHook.error,
        taskDistributionHook.error,
        workloadHook.error,
        equipmentHook.error,
        pontoDesviosHook.error,
        estoqueAlertasHook.error,
    ]);

    // Estado agregado de loading (true se qualquer hook estiver carregando)
    const isLoading = useMemo(() => {
        return Object.values(loadingStates).some(loading => loading === true);
    }, [loadingStates]);

    // Estado agregado de error (true se qualquer hook tiver erro)
    const isError = useMemo(() => {
        return Object.values(errorStates).some(error => error !== null);
    }, [errorStates]);

    // Array com todos os erros (filtra nulls)
    const errors = useMemo(() => {
        return Object.entries(errorStates)
            .filter(([_, error]) => error !== null)
            .map(([key, error]) => ({ source: key, message: error }));
    }, [errorStates]);

    // Função para refetch de todos os hooks
    const refetchAll = useMemo(() => {
        return async () => {
            const results = await Promise.allSettled([
                summaryHook.refetch(),
                taskDistributionHook.refetch(),
                workloadHook.refetch(),
                equipmentHook.refetch(),
                pontoDesviosHook.refetch(),
                estoqueAlertasHook.refetch(),
            ]);

            // Retornar resultados para análise
            return results.map((result, index) => ({
                key: ALL_DASHBOARD_QUERY_KEYS[index],
                status: result.status,
                value: result.status === 'fulfilled' ? result.value : null,
                reason: result.status === 'rejected' ? result.reason : null,
            }));
        };
    }, [
        summaryHook.refetch,
        taskDistributionHook.refetch,
        workloadHook.refetch,
        equipmentHook.refetch,
        pontoDesviosHook.refetch,
        estoqueAlertasHook.refetch,
    ]);

    // Função para invalidar todos os caches do dashboard
    const invalidateAll = useMemo(() => {
        return () => {
            ALL_DASHBOARD_QUERY_KEYS.forEach(queryKey => {
                queryClient.invalidateQueries({ queryKey });
            });
        };
    }, [queryClient]);

    // Função para invalidar caches específicos
    const invalidateSpecific = useMemo(() => {
        return (keys) => {
            const keysToInvalidate = Array.isArray(keys) ? keys : [keys];
            keysToInvalidate.forEach(queryKey => {
                if (ALL_DASHBOARD_QUERY_KEYS.some(key => JSON.stringify(key) === JSON.stringify(queryKey))) {
                    queryClient.invalidateQueries({ queryKey });
                }
            });
        };
    }, [queryClient]);

    return {
        data,
        isLoading,
        isError,
        errors,
        loadingStates,
        errorStates,
        refetchAll,
        invalidateAll,
        invalidateSpecific,
    };
};

/**
 * Função auxiliar para invalidar todos os caches do dashboard
 * Útil para usar fora de componentes React
 * 
 * @param {QueryClient} queryClient - Instância do QueryClient
 * 
 * @example
 * ```javascript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { invalidateDashboardCaches } from './useDashboardData';
 * 
 * const queryClient = useQueryClient();
 * 
 * // Após criar uma tarefa
 * await createTask(taskData);
 * invalidateDashboardCaches(queryClient);
 * ```
 */
export const invalidateDashboardCaches = (queryClient) => {
    ALL_DASHBOARD_QUERY_KEYS.forEach(queryKey => {
        queryClient.invalidateQueries({ queryKey });
    });
};

